"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Globe, Database, Calendar, Mail, MessageSquare, Star, Zap, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const trustBullets = [{
  id: 1,
  text: "No manual data entry"
}, {
  id: 2,
  text: "Works with your existing tools"
}, {
  id: 3,
  text: "Instant lead routing"
}];
const toolNodes = [{
  id: "website",
  name: "Website",
  icon: Globe,
  x: 90,
  y: 70
}, {
  id: "crm",
  name: "CRM",
  icon: Database,
  x: 310,
  y: 70
}, {
  id: "calendar",
  name: "Calendar",
  icon: Calendar,
  x: 340,
  y: 180
}, {
  id: "email",
  name: "Email",
  icon: Mail,
  x: 310,
  y: 290
}, {
  id: "sms",
  name: "SMS",
  icon: MessageSquare,
  x: 90,
  y: 290
}, {
  id: "reviews",
  name: "Reviews",
  icon: Star,
  x: 60,
  y: 180
}];
export default function Integration() {
  const shouldReduceMotion = useReducedMotion();
  const [activePulse, setActivePulse] = useState(0);
  useEffect(() => {
    if (shouldReduceMotion) return;
    const interval = setInterval(() => {
      setActivePulse(prev => (prev + 1) % toolNodes.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [shouldReduceMotion]);
  return <section id="cinematicintegrationhero" data-nav="dark" className="relative bg-background text-foreground pt-32 md:pt-44 pb-24 md:pb-36 border-b border-border overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-25 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start space-y-8">
            {}
            <div className="inline-flex items-center gap-2">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--accent-deep-red)] font-semibold">
                SYSTEM INTEGRATIONS
              </span>
            </div>

            {}
            <h1 className="text-4xl md:text-6xl font-default font-light tracking-tight leading-[1.1] text-foreground">
              {"Your Software Shouldn't Work Against You. Connect It Into One "}
              <span className="font-serif italic text-[var(--accent-deep-red)] font-normal">
                Seamless
              </span>
              {" System."}
            </h1>

            {}
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Disconnected tools waste hours, miss opportunities, require duplicate manual data entry, and cause leads to slip through cracks — ClearLine GEO connects existing tools so information flows automatically between platforms.
            </p>

            {}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-2">
              <Button size="lg" asChild className="bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white px-7 py-3.5 font-medium rounded-md shadow-md transition-all duration-300">
                <Link to="/PricingPlansPage">Get started now</Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-7 py-3.5 border-border hover:border-[var(--accent-deep-red)] transition-colors duration-300 font-medium rounded-md shadow-xs">
                <Link to="/VisibilityCheckupPage">
                  Free AI Visibility Checkup
                </Link>
              </Button>
            </div>

            <Separator className="my-2 max-w-xl opacity-40" />

            {}
            <div className="flex flex-wrap items-center gap-y-3 gap-x-6 pt-1 text-sm text-muted-foreground">
              {trustBullets.map((bullet, i) => <div data-index={i} key={bullet.id} className="flex items-center gap-2">
                  <div className="p-1 rounded-full bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)]">
                    <Check className="size-4 stroke-[1.25]" />
                  </div>
                  <span>{bullet.text}</span>
                </div>)}
            </div>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: "easeOut"
        }} className="lg:col-span-5 w-full">
            <div className="relative bg-card border border-border p-6 rounded-xl shadow-xl overflow-hidden">
              {}
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-border/60">
                <div className="flex items-center gap-2">
                  <div className="relative flex size-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent-deep-red)] opacity-75" />
                    <span className="relative inline-flex rounded-full size-2.5 bg-[var(--accent-deep-red)]" />
                  </div>
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    LIVE SYSTEM TOPOLOGY
                  </span>
                </div>
                <Badge variant="outline" className="font-mono text-xs gap-1.5 border-[var(--accent-deep-red)]/30 text-[var(--accent-deep-red)] bg-[var(--accent-deep-red)]/5">
                  <RefreshCw className="size-3 animate-spin text-[var(--accent-deep-red)]" />
                  SYNCED
                </Badge>
              </div>

              {}
              <div className="relative w-full h-[340px] flex items-center justify-center">
                {}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 360" fill="none">
                  {toolNodes.map((node, i) => {
                  const isCurrentActive = activePulse === i;
                  return <g data-index={i} key={`line-${node.id}`}>
                        {}
                        <line x1={node.x} y1={node.y} x2={200} y2={180} className="stroke-border" strokeWidth="1.5" strokeDasharray="4 4" />
                        {}
                        <motion.line x1={node.x} y1={node.y} x2={200} y2={180} className="stroke-[var(--accent-deep-red)]" strokeWidth={isCurrentActive ? "2.5" : "1.5"} opacity={isCurrentActive ? 0.9 : 0.4} initial={{
                      strokeDashoffset: 0
                    }} animate={shouldReduceMotion ? {} : {
                      strokeDashoffset: [0, -20]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear"
                    }} />
                      </g>;
                })}
                </svg>

                {}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center justify-center size-16">
                  <motion.div animate={shouldReduceMotion ? {} : {
                  scale: [1, 1.05, 1]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }} className="size-16 rounded-full bg-[var(--accent-deep-red)] text-white flex items-center justify-center shadow-lg border-2 border-background">
                    <Zap className="size-8" />
                  </motion.div>
                  <span className="absolute top-full mt-2 font-mono text-[10px] uppercase tracking-widest text-foreground font-semibold bg-background/80 px-2 py-0.5 rounded-sm border border-border whitespace-nowrap">
                    ClearLine Core
                  </span>
                </div>

                {}
                {toolNodes.map((node, i) => {
                const Icon = node.icon;
                const isCurrentActive = activePulse === i;
                return <motion.div data-index={i} key={node.id} style={{
                  position: "absolute",
                  left: `${node.x / 400 * 100}%`,
                  top: `${node.y / 360 * 100}%`,
                  transform: "translate(-50%, -50%)"
                }} className="z-10 flex flex-col items-center">
                      <div className={`size-11 rounded-lg flex items-center justify-center border transition-all duration-500 ${isCurrentActive ? "bg-[var(--accent-deep-red)]/15 border-[var(--accent-deep-red)] text-[var(--accent-deep-red)] shadow-md" : "bg-background border-border text-muted-foreground"}`}>
                        <Icon className="size-5" />
                      </div>
                      <span className="font-mono text-[11px] text-muted-foreground mt-1.5 font-medium bg-card px-1.5 py-0.5 rounded border border-border/40">
                        {node.name}
                      </span>
                    </motion.div>;
              })}
              </div>

              {}
              <div className="mt-2 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground font-mono">
                <span>STATUS: 100% AUTOMATED</span>
                <span className="text-[var(--accent-deep-red)] font-semibold">0 MANUAL ENTRY</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}